<template lang="pug">
#outing-detail(v-cloak)
  .box(v-if="outing")
    .box-header.with-border.register-header
      .title
        span {{ outing.targetResidentInfo.name }}（{{ outing.targetResidentInfo.roomName}}）様の届出詳細
    .d-flex.flex-row.flex-wrap-reverse
      .box-body.register-body
        .detail-content.d-flex.flex-column.mt-2
          FormLabel(key-name="届出種類" :value="_typeName")
          //- 外出外泊日時は曜日の色指定があるので、直接記載する
          .register-row.form-label
            .register-group
              .register-key
                span.key-name {{ _startAndDatetimeLabel }}
              .register-valu
                .d-flex.flex-row(v-if="outing.type == 'goingOut'")
                    span {{ getDate(outing.startDatetime) }}（
                    span(:class="weekCssClass(outing.startDatetime)") {{ getWeek(outing.startDatetime) }}）
                    span {{ getTime(outing.startDatetime) }}
                    span ～
                    span {{ getTime(outing.endDatetime) }}
                .d-flex.flex-row(v-if="outing.type == 'stayingOut'")
                    span {{ getDate(outing.startDatetime) }}（
                    span(:class="weekCssClass(outing.startDatetime)") {{ getWeek(outing.startDatetime) }}）
                    span {{ getTime(outing.startDatetime) }}
                    span.mr-1.ml-1 ～
                    span {{ getDate(outing.endDatetime) }}（
                    span(:class="weekCssClass(outing.endDatetime)") {{ getWeek(outing.endDatetime) }}）
                    span {{ getTime(outing.endDatetime) }}
          FormLabel(key-name="主な外出場所" :value="outing.location")
          FormLabel(key-name="申請者" :value="_applicantFamily" )
          FormLabel(key-name="お迎えに来る方（続柄）" :value="_applicantPickup")
          FormLabel(v-if="_showSelectedVehicle" key-name="外出手段" :value="outing.selectedVehicle")
          OutingDetailNeedsMeal(v-model="items" :fields="fields")
          FormLabel(key-name="家族からの連絡事項" :value="outing.text")
      .buttons.d-flex.flex-row.justify-content-end.flex-fill
        button.btn.edit-btn.m-3(type="button" @click="gotoOutingEdit") 修正
    .box-footer.register-footer
      .buttons.d-flex.flex-row.justify-content-end
        button.btn.cancel-btn(type="button" @click="gotoOutingList") キャンセル

  //- 画面全体ローディング
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";

import OutingsService, { NeedsMeal, OutingGetModel, OutingType } from "@/commons/services/Outings";
import OutingSettingsService, { OutingSettingGetModel } from "@/commons/services/OutingSettings";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import OutingDetailNeedsMeal from "@/pc/components/outings/OutingDetailNeedsMeal.vue";
import { OutingDetailNeedsMealsTableField, OutingDetailNeedsMealsTableItem } from "@/pc/containers/outings/OutingDetailNeedsMealsTable.vue";

@Component({
  components: {
    PageLoading,
    FormLabel,
    OutingDetailNeedsMeal
  }
})
export default class OutingDetail extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 外出外泊設定サービス */
  @Inject("OutingSettingsService") outingSettingsService!: OutingSettingsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 外出外泊届け */
  outing: OutingGetModel | null = null;
  /** 外出外泊届け設定 */
  setting: OutingSettingGetModel | null = null;

  /** 食事等のテーブル行 */
  items: Array<OutingDetailNeedsMealsTableItem> = [];
  /** 食事等のテーブル列 */
  fields: Array<OutingDetailNeedsMealsTableField> = [];

  /** ### 届出種類 */
  get _typeName(): string {
    if (!this.outing) {
      return "";
    }
    return this.outing.type == OutingType.GOING_OUT ? "外出" : this.outing.type == OutingType.STAYING_OUT ? "外泊" : "";
  }
  /** ### 外出外泊日時ラベル */
  get _startAndDatetimeLabel(): string {
    if (!this.outing) {
      return "";
    }
    if (this.outing.type == OutingType.GOING_OUT) {
      return "外出";
    } else if (this.outing.type == OutingType.STAYING_OUT) {
      return "外泊";
    } else {
      return "";
    }
  }
  /** ### 申請者 */
  get _applicantFamily(): string {
    if (!this.outing) {
      return "";
    }
    return `${this.outing.applicantFamilyInfo.name}（${this.outing.applicantFamilyInfo.relation}）`;
  }
  /** ### お迎えに来る方 */
  get _applicantPickup(): string {
    if (!this.outing) {
      return "";
    }
    // 申請者と同じ場合は固定文字列を返す
    if (this.outing.applicantPickup) {
      return "申請者と同じ";
    }
    // お迎えに来る方情報が存在する場合は家族名（続柄）を返す
    else if (this.outing.pickupPerson) {
      return `${this.outing.pickupPerson.name}（${this.outing.pickupPerson.relation}）`;
    } else {
      return "";
    }
  }
  /** 外出手段 */
  get _showSelectedVehicle(): boolean {
    if (!this.outing || !this.setting) {
      return false;
    } else {
      return this.setting.useSelectedVehicle;
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const id: number = Number(this.$route.params.id);
    const outing: OutingGetModel = await this.getOuting(id);
    this.outing = outing;

    const setting: OutingSettingGetModel = await this.getOutingSetting();
    this.setting = setting;

    const fields: Array<OutingDetailNeedsMealsTableField> = this.createFields(setting);
    this.fields = fields;
    const items: Array<OutingDetailNeedsMealsTableItem> = this.createItems(outing);
    this.items = items;

    this.isBusy = false;
    return;
  }

  /**
   * ### 外出外泊届けを取得する
   */
  public async getOuting(id: number): Promise<OutingGetModel> {
    const response: OutingGetModel = await this.outingsService.get(userStore, id);
    return response;
  }

  /**
   * ### 外出外泊届け設定取得
   */
  public async getOutingSetting(): Promise<OutingSettingGetModel> {
    const response: OutingSettingGetModel = await this.outingSettingsService.get(userStore);
    return response;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(setting: OutingSettingGetModel): Array<OutingDetailNeedsMealsTableField> {
    const fields: Array<OutingDetailNeedsMealsTableField> = [];
    fields.push({ key: "date", label: "日", sortable: false, sortDirection: "desc", class: "date text-center" });

    // 食事列は設定に応じて表示／非表示を切り替える
    if (setting.useNeedsMeal.breakfast) {
      fields.push({ key: "breakfast", label: "朝食", sortable: false, sortDirection: "desc", class: "meal-name breakfast text-center" });
    }
    if (setting.useNeedsMeal.lunch) {
      fields.push({ key: "lunch", label: "昼食", sortable: false, sortDirection: "desc", class: "meal-name lunch text-center" });
    }
    if (setting.useNeedsMeal.snack) {
      fields.push({ key: "snack", label: "おやつ", sortable: false, sortDirection: "desc", class: "meal-name snack text-center" });
    }
    if (setting.useNeedsMeal.dinner) {
      fields.push({ key: "dinner", label: "夕食", sortable: false, sortDirection: "desc", class: "meal-name dinner text-center" });
    }
    return fields;
  }

  /**
   * ### テーブル行を作成する
   *
   * - 外出の場合は開始日のみ
   * - 外泊の場合は終了日も追加
   */
  public createItems(outing: OutingGetModel): Array<OutingDetailNeedsMealsTableItem> {
    const items: Array<OutingDetailNeedsMealsTableItem> = [];
    items.push({
      datetime: outing.startDatetime,
      needsMeal: outing.startDateNeedsMeal
    });
    if (outing.type == OutingType.STAYING_OUT) {
      items.push({
        datetime: outing.endDatetime,
        needsMeal: outing.endDateNeedsMeal as NeedsMeal
      });
    }
    return items;
  }
  /**
   * ### 時分を返す
   */
  public getTime(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "hh:MM");
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "yyyy年mm月dd日", false);
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(date: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### 外出外泊届け修正画面へ遷移する
   */
  public gotoOutingEdit(): void {
    const id: number = Number(this.$route.params.id);
    this.$router.push({ name: PcUrl.OutingEdit, params: { id: `${id}` } });
    return;
  }

  /**
   * ### 外出外泊届け一覧画面へ遷移する
   */
  public gotoOutingList(): void {
    this.$router.push(PcUrl.OutingList);
    return;
  }
}
</script>

<style lang="scss" scoped>
#outing-detail {
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;

  .detail-content {
    max-width: 600px;
    min-width: 600px;
  }

  .edit-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
